import React from "react";

import OutcomesV2 from "shared/components/vega/LumenV2/OutcomesV2";

import { useDeviceType } from "shared/hooks";

import {
  categoryGroups,
  backgroundCheckCategories,
  identityVerificationCategories,
} from "../constants";

import { findLumenResult } from "../queryHelpers";

import { categoryProcessorMap } from "./helpers/categoryProcessors";
import LumenEntryCard from "./LumenEntryCard";

const LumenSummary = ({
  lumenRun,
  applicants,
  isLumenStillCalculating,
  isIdentityVerificationEnabled,
  backgroundAndCreditChecksEnabled,
}) => {
  const { isMobile } = useDeviceType();
  // Responsive styles based on device type
  const containerStyles = {
    display: "flex",
    flexWrap: "wrap" as "wrap",
    justifyContent: "center",
    alignItems: "stretch", // Ensure all items have the same height
    gap: "4px",
    width: "100%",
  };
  // For mobile, each card takes full width
  // For tablet and desktop, all cards have the same fixed width and height
  const cardContainerStyles = {
    flex: isMobile ? "1 1 100%" : "0 0 280px", // Fixed width for non-mobile
    minWidth: isMobile ? "100%" : "280px",
    maxWidth: isMobile ? "100%" : "280px", // Same as minWidth to ensure equal sizing
    minHeight: "100px", // Set a minimum height for all cards
    display: "flex",
    margin: "8px",
  };

  // Get the overall application result (not per-applicant)
  const applicationLumenResult = findLumenResult(lumenRun);

  // Generate results for each applicant
  const applicantLumenResults =
    applicants?.map((applicant) => {
      const result = findLumenResult(lumenRun, applicant.id);
      return {
        applicantId: applicant.id,
        applicantName: `${applicant.firstName} ${applicant.lastName}`,
        lumenResult: result,
      };
    }) || [];

  // Filter category groups based on whether background checks are enabled
  const filteredCategoryGroups = categoryGroups.filter(({ id }) => {
    if (!isIdentityVerificationEnabled) {
      return !identityVerificationCategories.includes(id);
    }
    if (!backgroundAndCreditChecksEnabled) {
      // When background checks are not enabled, exclude background check categories
      return !backgroundCheckCategories.includes(id);
    }
    return true;
  });

  return (
    <div className="vega container">
      <section className="vega flex content" style={containerStyles}>
        {filteredCategoryGroups.map(({ id, name }) => {
          const processor = categoryProcessorMap[id];
          const { label, status, details } = processor({
            applicationLumenResult,
            applicantLumenResults,
            isLumenStillCalculating,
            lumenRun,
          });
          return (
            <div key={id} style={cardContainerStyles}>
              <LumenEntryCard
                title={name}
                label={label}
                status={status}
                details={details}
              />
            </div>
          );
        })}
      </section>
      <OutcomesV2 lumenRun={lumenRun} />
    </div>
  );
};

export default LumenSummary;
